import type { Claim, MatterFullState } from '@/types';

/**
 * Client-side health evaluation for claim cards in Stage 3.
 *
 * Seven checks per claim — executed after the server has already run its
 * renumber + reference-rewrite pass (``backend/claim_tree.py``), so the
 * text we see here is the *post-adjustment* text. We don't second-guess
 * the server; we just surface remaining issues.
 *
 * Every issue carries an optional ``highlight_text`` — the exact literal
 * substring in ``claim.claim_text`` that the UI should wrap in a yellow
 * ``<mark>`` span. Callers that render the body pass the returned
 * issues through ``highlightSegments()`` below.
 */

export type CheckId = 1 | 2 | 3 | 4 | 5 | 6 | 7;

export interface HealthIssue {
  check_id: CheckId;
  check_label: string;
  passed: boolean;
  message: string;
  highlight_text?: string;
  severity: 'warn' | 'advisory';
}

export interface HealthResult {
  claim_id: string;
  status: 'ok' | 'warn';
  issues: HealthIssue[];
  failed_checks: CheckId[];
}

export interface EvaluateOptions {
  idfTexts?: string[];
}

// ─── Shared helpers ──────────────────────────────────────────────────────

const CLAIM_REF_RE = /\bclaim\s+(\d+)\b/gi;
const PLACEHOLDER_RE = /(\[TBD\]|\[PLACEHOLDER\]|\[\.\.\.\]|\[[^\]\n]{0,40}\])/gi;
const SPECIFY_TOKEN_RE = /\u27e6SPECIFY:[^\u27e7]+\u27e7/g;

function plainBody(text: string): string {
  // Strip SPECIFY tokens so checks don't trip over placeholders — those
  // have their own badge + count in the claim header.
  return (text || '').replace(SPECIFY_TOKEN_RE, '');
}

// ─── Check 1 — dependency references resolve ────────────────────────────

function checkDependencyReferences(
  claim: Claim,
  allClaims: Claim[],
): HealthIssue | null {
  const numbers = new Set(allClaims.map((c) => c.claim_number));
  const text = plainBody(claim.claim_text);
  let match: RegExpExecArray | null;
  // Fresh regex per call so lastIndex doesn't leak across claims.
  const re = new RegExp(CLAIM_REF_RE.source, CLAIM_REF_RE.flags);
  while ((match = re.exec(text)) !== null) {
    const n = parseInt(match[1], 10);
    // Self-references are legal ("as described in claim N" inside claim N
    // is unusual but not a structural error) — skip only truly missing N.
    if (!numbers.has(n)) {
      return {
        check_id: 1,
        check_label: 'Dependency reference',
        passed: false,
        message: `Dependency reference to Claim ${n} not found`,
        highlight_text: match[0],
        severity: 'warn',
      };
    }
  }
  return null;
}

// ─── Check 2 — preamble present ─────────────────────────────────────────

// Multiple-dependent forms ("according to any one of the preceding
// claims", "of any one of claims 1 to 5") are standard in EP and PCT
// drafting, so a parent reference need not be a bare "claim N".
const DEP_PREAMBLE_RE = /\b(?:of|in|to|per|according\s+to|as\s+(?:claimed|recited|set\s+forth|defined|described)\s+in|as\s+in|as\s+claimed\s+by)\s+(?:any\s+(?:one\s+)?(?:of\s+)?)?(?:the\s+)?(?:claims?\s+\d+|(?:preceding|previous|foregoing)\s+claims?\b)/i;

function checkPreamble(claim: Claim): HealthIssue | null {
  const text = plainBody(claim.claim_text).trim();
  if (!text) {
    return {
      check_id: 2,
      check_label: 'Preamble',
      passed: false,
      message: 'Claim body is empty',
      severity: 'warn',
    };
  }
  if (claim.claim_type === 'independent') {
    // Strip leading whitespace + claim number prefix like "1." if present.
    const stripped = text.replace(/^\s*\d+\s*\.\s*/, '');
    const ok = /^(A|An|The)\b/.test(stripped);
    if (!ok) {
      const preambleSnippet = stripped.split(/[,.]/, 1)[0].slice(0, 60).trim();
      return {
        check_id: 2,
        check_label: 'Preamble',
        passed: false,
        message: 'Independent claim should start with "A", "An", or "The"',
        highlight_text: preambleSnippet || undefined,
        severity: 'warn',
      };
    }
    return null;
  }
  // Dependent claims: expect "The <thing> of claim N, wherein/further
  // comprising" or "As claimed in claim N". We accept any phrasing that
  // references a parent claim — ``checkDependencyReferences`` catches
  // the harder case where the reference is missing entirely.
  const hasDepPreamble = DEP_PREAMBLE_RE.test(text);
  if (!hasDepPreamble) {
    const preambleSnippet = text.split(',', 1)[0].slice(0, 80).trim();
    return {
      check_id: 2,
      check_label: 'Preamble',
      passed: false,
      message: 'Dependent claim is missing a parent-claim preamble (e.g. "The system of claim 1, wherein …")',
      highlight_text: preambleSnippet || undefined,
      severity: 'warn',
    };
  }
  return null;
}

// ─── Check 3 — at least one limitation keyword ──────────────────────────

function checkLimitation(claim: Claim): HealthIssue | null {
  const text = plainBody(claim.claim_text).toLowerCase();
  const hasKeyword = /\b(comprising|wherein|configured to|including)\b/.test(text);
  if (!hasKeyword) {
    // Highlight the first sentence so the drafter knows where the body
    // is expected to continue.
    const firstSentence = plainBody(claim.claim_text).split(/[.;]/, 1)[0].slice(0, 120).trim();
    return {
      check_id: 3,
      check_label: 'Limitation keyword',
      passed: false,
      message: 'Claim body should contain "comprising", "wherein", "configured to", or "including"',
      highlight_text: firstSentence || undefined,
      severity: 'warn',
    };
  }
  return null;
}

// ─── Check 4 — no bracket placeholders ──────────────────────────────────

function checkPlaceholder(claim: Claim): HealthIssue | null {
  const text = plainBody(claim.claim_text);
  const match = PLACEHOLDER_RE.exec(text);
  // Reset lastIndex so the shared regex doesn't leak state.
  PLACEHOLDER_RE.lastIndex = 0;
  if (match) {
    return {
      check_id: 4,
      check_label: 'Placeholder text',
      passed: false,
      message: `Placeholder text detected: ${match[0]}`,
      highlight_text: match[0],
      severity: 'warn',
    };
  }
  return null;
}

// ─── Check 4b — unresolved SPECIFY tokens (⟦SPECIFY:…⟧) ─────────────────
//
// SPECIFY tokens are the "manual info still needed" placeholders the
// backend inserts when the IDF doesn't disclose a numeric range,
// component name, etc. They render as clickable amber chips in the
// claim body (see PlaceholderChip in Stage3.tsx) and the claim-navigator
// row already shows their count in an amber pill. We add them to the
// health issues here too so:
//   1. the tree-row health pip flips to amber (matches the pill),
//   2. the hover tooltip lists the specific SPECIFY hints so the
//      drafter knows WHAT manual info is still needed before clicking
//      into the card.
// Inline, the PlaceholderChip already owns the visual — we deliberately
// do NOT return a `highlight_text`, so ClaimBodyRenderer won't add a
// redundant yellow <mark> around the SPECIFY text (the chip already
// highlights it in amber).
function checkSpecifyTokens(claim: Claim): HealthIssue | null {
  const raw = claim.claim_text || '';
  const hits: string[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(SPECIFY_TOKEN_RE.source, 'g');
  while ((m = re.exec(raw)) !== null) {
    hits.push(m[0]);
  }
  const countFromText = hits.length;
  // Trust whichever source reports more — the backend stores a
  // placeholder_count column that's authoritative when text parsing
  // can't match (e.g. UTF-8 mangling through a proxy).
  const count = Math.max(countFromText, claim.placeholder_count || 0);
  if (count === 0) return null;
  const preview = hits.slice(0, 3).map((h) => {
    // "⟦SPECIFY: numerical range for accuracy⟧" → "numerical range for accuracy"
    const inner = h.replace(/^\u27e6SPECIFY:\s*/, '').replace(/\u27e7$/, '').trim();
    return inner ? `• ${inner}` : `• ${h}`;
  });
  const extra = count - preview.length;
  const message = [
    `${count} unresolved SPECIFY placeholder${count === 1 ? '' : 's'} — manual info still needed`,
    ...preview,
    ...(extra > 0 ? [`• … and ${extra} more`] : []),
  ].join('\n');
  return {
    check_id: 4,
    check_label: 'SPECIFY placeholders',
    passed: false,
    message,
    severity: 'warn',
  };
}

// ─── Check 5 — claim number consistent with position ────────────────────

function checkClaimNumber(claim: Claim): HealthIssue | null {
  // "1. A processor comprising …" — the leading integer should match
  // claim.claim_number. Many drafts don't include the number at all,
  // which is fine — we only flag mismatches when a number IS present.
  const m = /^\s*(\d+)\s*\.?\s/.exec(claim.claim_text || '');
  if (m) {
    const printed = parseInt(m[1], 10);
    if (printed !== claim.claim_number) {
      return {
        check_id: 5,
        check_label: 'Claim number',
        passed: false,
        message: `Claim text starts with number ${printed} but this is Claim ${claim.claim_number}`,
        highlight_text: m[0].trim(),
        severity: 'warn',
      };
    }
  }
  return null;
}

// ─── Check 6 — IDF grounding advisory ───────────────────────────────────

const _STOPWORDS = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'onto',
  'upon', 'wherein', 'comprising', 'including', 'configured', 'based',
  'claim', 'system', 'method', 'apparatus', 'device', 'module', 'said',
  'thereof', 'therein', 'whereby', 'further', 'first', 'second', 'third',
]);

function _candidateTerms(text: string): string[] {
  // Pull out capitalised multi-word noun-like phrases: they're the most
  // likely to be product-specific identifiers that need to match IDF.
  const matches = text.match(/\b([A-Z][A-Za-z0-9]+(?:\s+[A-Z][A-Za-z0-9]+)+)\b/g);
  if (!matches) return [];
  return Array.from(new Set(
    matches
      .map((m) => m.trim())
      .filter((m) => m.split(/\s+/).every((w) => !_STOPWORDS.has(w.toLowerCase()))),
  ));
}

function checkIdfGrounding(
  claim: Claim,
  options: EvaluateOptions,
): HealthIssue | null {
  const texts = options.idfTexts || [];
  // Spec: if no IDF is indexed, skip silently — no error, no warning.
  if (texts.length === 0) return null;
  const joined = texts.join('\n').toLowerCase();
  if (!joined.trim()) return null;
  const body = plainBody(claim.claim_text);
  const candidates = _candidateTerms(body);
  if (candidates.length === 0) return null;
  const missing = candidates.find((term) => !joined.includes(term.toLowerCase()));
  if (!missing) return null;
  return {
    check_id: 6,
    check_label: 'IDF grounding',
    passed: false,
    message: `Unverified element — "${missing}" not found in any indexed IDF`,
    highlight_text: missing,
    severity: 'advisory',
  };
}

// ─── Check 7 — antecedent basis ─────────────────────────────────────────
//
// "the X" or "said X" needs an earlier "a X" in this claim or in a claim
// it depends on. A steer that turns claim 1's "a forward deceleration"
// into "a deceleration" leaves the dependents saying "the forward
// deceleration", and no other check noticed. This is a word-level
// heuristic tuned to stay quiet: a missed gap costs less than a card full
// of false warnings, so anything ambiguous counts as introduced.

export interface AntecedentGap {
  /** Lower-case words after the article, e.g. "forward deceleration". */
  phrase: string;
  /** The span as written in the claim, e.g. "the forward deceleration". */
  text: string;
}

const wordSet = (words: string) => new Set(words.split(' '));

// Words that end a noun phrase and are never part of one.
const PHRASE_STOP = wordSet(
  'a an the said each every any all of to in on at for with by from into onto over under ' +
  'between through within via per as than that which who whose wherein whereby where when ' +
  'while if and or but not is are was were be been being has have had having comprises ' +
  'comprising comprise includes including include consists consisting configured arranged ' +
  'adapted operable so such it its their them they this these those same one only also ' +
  'further claim claims according above below along about across after before during upon ' +
  'toward towards around against behind beyond without until unless among amongst thereof ' +
  'therein thereto therefrom thereby therewith thereon',
);

// "the preceding claims", "the step of", "the present invention", "the
// other …", "the lowest cost", "the total weight": idiom rather than a
// reference to an introduced element.
const REFERENCE_SKIP = wordSet(
  'preceding previous foregoing above following same claim claims step steps method ' +
  'invention present like other art time claimed aforementioned aforesaid latter former ' +
  'respective remaining rest entire whole total overall maximum minimum average ' +
  'best worst most least highest lowest greatest ' +
  'largest smallest closest nearest farthest furthest earliest latest shortest longest fewest',
);

const NUMBER_WORDS = wordSet('one two three four five six seven eight nine ten eleven twelve');

// Words that follow a noun phrase rather than belong to it: verbs ("the
// deceleration persists") and post-nominal modifiers ("a signal
// indicative of", "a speed greater than").
const PHRASE_TAIL = wordSet(
  'exceeds exceed persists persist confirms confirm receives receive transmits transmit ' +
  'generates generate determines determine detects detect indicates indicate represents ' +
  'represent corresponds correspond extends extend rotates rotate remains remain reaches ' +
  'triggers activates deactivates emits emit produces produce provides provide defines ' +
  'define measures senses stores sends causes occurs occur increases decreases varies vary ' +
  'contains contain holds carries surrounds surround engages engage abuts abut operates ' +
  'operate performs perform executes execute computes compute calculates calculate compares ' +
  'compare connects connect communicates communicate illuminates illuminate equals enables ' +
  'enable prevents prevent allows allow requires require uses using used ' +
  'indicative representative responsive relative proportional equivalent adjacent proximate ' +
  'capable suitable sufficient greater less lower higher larger smaller longer shorter ' +
  'made held built known shown given taken sent kept found driven chosen written drawn',
);

// "-ing" words that name a thing, so they end a phrase rather than trail it.
const ING_NOUNS = wordSet(
  'housing bearing casing coating opening setting reading wiring spring string thing swing ' +
  'sling lighting timing mounting fitting building heating cooling sealing recording marking ' +
  'winding coupling damping spacing rating ending bushing tubing lining ceiling processing ' +
  'braking steering charging sensing signalling signaling',
);
const ADVERB_RE = /(?:ally|ively|ously|ently|antly|edly|ably|ibly|ctly|tely|ully)$/;

function trailsNounPhrase(ws: string[], k: number): boolean {
  if (k === 0) return false;
  const w = ws[k];
  if (PHRASE_TAIL.has(w) || ADVERB_RE.test(w)) return true;
  // A participle trails the phrase unless a noun follows it, as in "a
  // computer implemented method" or "a speed sensing unit".
  const next = ws[k + 1];
  return isParticiple(w) && (next === undefined || /^\d/.test(next));
}

function isParticiple(w: string): boolean {
  return w.length > 4 && ((w.endsWith('ing') && !ING_NOUNS.has(w)) || /[^e]ed$/.test(w));
}

// How many leading words are the noun phrase proper ("forward
// deceleration persists" → 2). Used for what we report, never for
// matching: matching works on the full run so a misjudged verb cannot
// create a gap.
function nounPart(ws: string[]): number {
  for (let k = 1; k < ws.length; k++) {
    if (trailsNounPhrase(ws, k)) return k;
  }
  return ws.length;
}

// Naive singular: "sensors" ~ "sensor", "lenses" ~ "lens", "batteries" ~ "battery".
function wordForms(w: string): string[] {
  const forms = [w];
  if (w.length > 3 && w.endsWith('s') && !w.endsWith('ss')) {
    forms.push(w.slice(0, -1));
    if (w.endsWith('ies')) forms.push(`${w.slice(0, -3)}y`);
    else if (w.endsWith('es')) forms.push(w.slice(0, -2));
  }
  return forms;
}

function sameWord(a: string, b: string): boolean {
  if (a === b) return true;
  const bForms = wordForms(b);
  return wordForms(a).some((f) => bForms.includes(f));
}

function sameWords(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((w, k) => sameWord(w, b[k]));
}

// A generic last word lets the word before it stand alone: "a computer
// program product" introduces "the program" (the EPO's own wording).
const GENERIC_HEADS = wordSet(
  'product assembly system unit device arrangement kit module apparatus',
);

// Whether the noun phrase in ``ws`` may end just before word ``k``. Only
// matching asks this, so it is looser than ``nounPart``: any participle
// or "-able" word may trail ("a flange extending radially", "a lever
// pivotable about", "a belt coupling the motor"), and so may a final "-s"
// word, which is usually a verb ("a spring urges the lever").
function canEndBefore(ws: string[], k: number): boolean {
  const w = ws[k];
  return trailsNounPhrase(ws, k)
    || (w.length > 4 && /(?:ing|[^e]ed)$/.test(w))
    || (w.length > 6 && /(?:able|ible)$/.test(w))
    || (k === ws.length - 1 && (GENERIC_HEADS.has(w) || /[^s]s$/.test(w)));
}

// "a wheel speed sensor" introduces "the sensor" and "the sensor output":
// a tail of the introduced words must equal a head of the referenced
// ones. The tail may also stop just before a word that trails the noun
// phrase ("a signal indicative of …" introduces "the signal").
function introduces(intro: string[], ref: string[]): boolean {
  for (let end = intro.length; end > 0; end--) {
    if (end < intro.length && !canEndBefore(intro, end)) continue;
    for (let n = 1; n <= Math.min(end, ref.length); n++) {
      if (sameWords(intro.slice(end - n, end), ref.slice(0, n))) return true;
    }
  }
  return false;
}

// Whether ``ref`` is the noun phrase of ``intro`` with more words in front
// ("forward deceleration" against "a deceleration exceeding …").
function extendsIntro(intro: string[], ref: string[]): boolean {
  const core = intro.slice(0, nounPart(intro));
  return core.length < ref.length && sameWords(core, ref.slice(-core.length));
}

interface Word { w: string; start: number; end: number }
// null stands for punctuation or a line break, which ends a phrase.
type Token = Word | null;

const TOKEN_RE = /\d+(?:\.\d+)+|[A-Za-z0-9]+(?:['\u2019][A-Za-z]+)?|[,;:.()\n\r]/g;
const BREAK_RE = /^[,;:.()\n\r]$/;

function tokenise(text: string): Token[] {
  const out: Token[] = [];
  const re = new RegExp(TOKEN_RE.source, 'g');
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const raw = m[0];
    out.push(BREAK_RE.test(raw) ? null : {
      // "the user's hand" refers to the user.
      w: raw.toLowerCase().replace(/['\u2019]s$/, ''),
      start: m.index,
      end: m.index + raw.length,
    });
  }
  return out;
}

const isNumber = (w?: string) => !!w && (NUMBER_WORDS.has(w) || /^\d/.test(w));
const isBareWord = (t: Token | undefined) => !!t && !PHRASE_STOP.has(t.w) && !isNumber(t.w);

// "one or more", "at least one", "a plurality of", "two": these introduce
// a phrase the way "a" does, and "the" can carry them too ("the two
// sensors", "the at least one sensor").
function quantifierLength(toks: Token[], i: number): number {
  const w = (k: number) => toks[i + k]?.w;
  if (w(0) === 'a' && w(1) === 'plurality' && w(2) === 'of') return 3;
  if (w(0) === 'plurality' && w(1) === 'of') return 2;
  if (w(0) === 'at' && w(1) === 'least' && isNumber(w(2))) return 3;
  if (isNumber(w(0))) return w(1) === 'or' && w(2) === 'more' ? 3 : 1;
  return 0;
}

function phraseAt(toks: Token[], i: number): Word[] {
  const out: Word[] = [];
  for (let k = i; k < toks.length && out.length < 6; k++) {
    const t = toks[k];
    if (!t || PHRASE_STOP.has(t.w)) break;
    out.push(t);
  }
  return out;
}

interface Introduction { ws: string[]; start: number }

interface Reference {
  // The words after the article, then looser readings an introduction
  // may match instead: "the first and second sensors" as "first sensors",
  // "the predicted trajectories" as "trajectories".
  readings: string[][];
  start: number;
  end: number;
  reported: string[];
  // "The light according to claim 1": the subject of the named claim.
  namesClaim: boolean;
  // "the outer surface of the housing": an inherent part of something
  // named next, which needs no "a" of its own.
  inherent: boolean;
}

interface Mentions {
  intros: Introduction[];
  refs: Reference[];
  subjectHeads: string[];
}

// Reuses the dependent-preamble pattern, anchored to what follows a phrase.
const NAMES_CLAIM_RE = new RegExp(`^\\s*${DEP_PREAMBLE_RE.source}`, 'i');

// Stage 3 evaluates every claim against the whole set, so each parent's
// text is scanned once per child; remember recent scans by text.
const scanCache = new Map<string, Mentions>();

function scanMentions(claimText: string): Mentions {
  const key = claimText || '';
  let hit = scanCache.get(key);
  if (!hit) {
    if (scanCache.size >= 500) scanCache.clear();
    hit = scanText(key);
    scanCache.set(key, hit);
  }
  return hit;
}

function scanText(claimText: string): Mentions {
  // Blank SPECIFY tokens in place so offsets still index claim_text.
  const text = (claimText || '').replace(SPECIFY_TOKEN_RE, (m) => ' '.repeat(m.length));
  const toks = tokenise(text);
  const intros: Introduction[] = [];
  const refs: Reference[] = [];
  let subject: string[] = [];

  // The claim's subject is the phrase its first word opens ("A bicycle
  // light comprising", "The bicycle light according to claim 1"), past
  // any leading "1." claim number.
  let first = toks.findIndex((t) => t !== null);
  if (first >= 0 && /^\d+$/.test(toks[first]!.w) && toks[first + 1] === null) {
    first = toks.findIndex((t, k) => k > first && t !== null);
  }

  let i = 0;
  while (i < toks.length) {
    const t = toks[i];
    if (!t) { i += 1; continue; }

    if (t.w === 'the' || t.w === 'said') {
      const at = i + 1 + quantifierLength(toks, i + 1);
      const phrase = phraseAt(toks, at);
      if (phrase.length === 0) { i += 1; continue; }
      const ws = phrase.map((p) => p.w);
      const after = at + phrase.length;
      if (i === first) subject = ws;
      i = after;
      const n = nounPart(ws);
      // What trails the noun phrase is bare: "the controller monitors
      // fluid pressure" introduces the pressure.
      if (n < ws.length) intros.push({ ws: ws.slice(n), start: phrase[n].start });
      if (REFERENCE_SKIP.has(ws[0])) continue;
      const namesClaim = NAMES_CLAIM_RE.test(text.slice(phrase[phrase.length - 1].end));
      const link = toks[after]?.w;
      const inherent = !namesClaim && (link === 'of' || link === 'thereof');
      const readings = [ws];
      if ((link === 'and' || link === 'or') && isBareWord(toks[after + 1])) {
        const more = phraseAt(toks, after + 1).map((p) => p.w);
        readings.push([...ws, more[nounPart(more) - 1]]);
      }
      if (ws.length > 1 && isParticiple(ws[0])) readings.push(ws.slice(1));
      refs.push({
        readings, namesClaim, inherent,
        start: t.start, end: phrase[n - 1].end, reported: ws.slice(0, n),
      });
      continue;
    }

    // Anything else that is not a stop word opens an introduction: after
    // "a" or a quantifier, or bare, which covers mass and plural nouns
    // ("comprising circuitry", "to circulate coolant", "a set of
    // sensors") and method steps ("; and determining").
    const q = quantifierLength(toks, i) || (t.w === 'a' || t.w === 'an' ? 1 : 0);
    if (!q && PHRASE_STOP.has(t.w)) { i += 1; continue; }
    const at = i + q;
    const phrase = phraseAt(toks, at);
    if (phrase.length === 0) { i = at; continue; }
    const ws = phrase.map((p) => p.w);
    if (i === first) subject = ws;
    intros.push({ ws, start: t.start });
    i = at + phrase.length;
  }

  const subjectHeads = subject.filter(
    (_, k) => k === subject.length - 1 || canEndBefore(subject, k + 1),
  );
  return { intros, refs, subjectHeads };
}

function parentNumbers(c: Claim): number[] {
  if (c.parent_claim_numbers && c.parent_claim_numbers.length > 0) return c.parent_claim_numbers;
  return c.parent_claim_number != null ? [c.parent_claim_number] : [];
}

// "claim 2", "claims 1 to 3", "claims 1-3", "claims 1, 2 or 4".
const CITED_RE = /\bclaims?\s+(\d+(?:\s*(?:,\s*(?:or|and)?|or|and|to|through|-|–)\s*(?:claims?\s+)?\d+)*)/gi;
const PRECEDING_RE = /\b(?:preceding|previous|foregoing)\s+claims?\b/i;

// The claims among ``numbers`` that a claim's text cites; "any preceding
// claim" cites every lower number.
function citedClaims(claim: Claim, numbers: number[]): number[] {
  const text = plainBody(claim.claim_text);
  const out = new Set<number>();
  const cite = (lo: number, hi: number) => {
    for (const n of numbers) if (n >= lo && n <= hi && n !== claim.claim_number) out.add(n);
  };
  if (PRECEDING_RE.test(text)) cite(0, claim.claim_number - 1);
  const re = new RegExp(CITED_RE.source, CITED_RE.flags);
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    // Each item is one claim or a range ("1 to 3").
    for (const item of m[1].split(/\s*(?:,|\bor\b|\band\b)\s*/i)) {
      const [lo, hi = lo] = (item.match(/\d+/g) || []).map(Number);
      if (lo !== undefined) cite(lo, hi);
    }
  }
  return Array.from(out);
}

// What a claim builds on: its parents. An independent claim stands on
// its own text unless it cites other claims, as "A process for preparing
// the composition according to any one of claims 1 to 3" does (the
// backend only reads "claim N" as a dependency, so these stay
// independent).
function basesOf(c: Claim, numbers: number[]): number[] {
  return c.claim_type === 'independent' ? citedClaims(c, numbers) : parentNumbers(c);
}

// Every claim this one builds on, directly or through its bases.
function ancestorsOf(claim: Claim, byNumber: Map<number, Claim>): Claim[] {
  const numbers = Array.from(byNumber.keys());
  const out: Claim[] = [];
  const seen = new Set<number>([claim.claim_number]);
  const queue = basesOf(claim, numbers).slice();
  while (queue.length > 0) {
    const n = queue.shift() as number;
    if (seen.has(n)) continue;
    seen.add(n);
    const c = byNumber.get(n);
    if (!c) continue;
    out.push(c);
    queue.push(...basesOf(c, numbers));
  }
  return out;
}

export function findAntecedentGaps(claim: Claim, allClaims: Claim[]): AntecedentGap[] {
  const own = scanMentions(claim.claim_text);
  if (own.refs.length === 0) return [];
  const byNumber = new Map<number, Claim>();
  for (const c of allClaims) byNumber.set(c.claim_number, c);
  const scanned = new Map<number, Mentions>(
    ancestorsOf(claim, byNumber)
      .map((c): [number, Mentions] => [c.claim_number, scanMentions(c.claim_text)]),
  );
  const inherited = Array.from(scanned.values()).flatMap((m) => m.intros);

  // A subject followed by "according to claim N" is checked against the
  // subjects of the claims it depends on, and of any claim it names.
  if (own.refs.some((r) => r.namesClaim)) {
    for (const n of citedClaims(claim, Array.from(byNumber.keys()))) {
      if (!scanned.has(n)) scanned.set(n, scanMentions(byNumber.get(n)!.claim_text));
    }
  }
  const subjectHeads = Array.from(scanned.values()).flatMap((m) => m.subjectHeads);

  const gaps: Reference[] = [];
  for (const ref of own.refs) {
    const ws = ref.readings[0];
    if (ref.namesClaim && subjectHeads.some((h) => sameWord(h, ws[ws.length - 1]))) continue;
    const pool = inherited.concat(own.intros.filter((p) => p.start < ref.start));
    // An inherent part still needs its own "a" when it is an introduced
    // element with words added in front, as when a steer drops a
    // modifier: after "a deceleration", "the forward deceleration of the
    // bicycle" is a gap. "The rear wheel of the bicycle" after "a front
    // wheel" is not.
    if (ref.inherent && !pool.some((p) => extendsIntro(p.ws, ref.reported))) continue;
    const introduced = pool.some((p) => ref.readings.some((r) => introduces(p.ws, r)));
    if (introduced || gaps.some((g) => sameWords(g.reported, ref.reported))) continue;
    gaps.push(ref);
  }
  return gaps.map((g) => ({
    phrase: g.reported.join(' '),
    text: (claim.claim_text || '').slice(g.start, g.end),
  }));
}

function checkAntecedentBasis(claim: Claim, allClaims: Claim[]): HealthIssue | null {
  const gaps = findAntecedentGaps(claim, allClaims);
  if (gaps.length === 0) return null;
  const named = gaps.slice(0, 2).map((g) => `"${g.text}"`);
  const extra = gaps.length - named.length;
  const list = extra > 0 ? `${named.join(', ')} and ${extra} more` : named.join(' and ');
  const standsAlone = claim.claim_type === 'independent'
    && citedClaims(claim, allClaims.map((c) => c.claim_number)).length === 0;
  const scope = standsAlone ? 'this claim' : 'this claim or the claims it depends on';
  return {
    check_id: 7,
    check_label: 'Antecedent basis',
    passed: false,
    message: `No earlier "a …" for ${list} in ${scope}.`,
    highlight_text: gaps[0].text,
    severity: 'warn',
  };
}

// ─── Public API ─────────────────────────────────────────────────────────

export function evaluateClaimHealth(
  claim: Claim,
  allClaims: Claim[],
  options: EvaluateOptions = {},
): HealthResult {
  const runners: Array<() => HealthIssue | null> = [
    () => checkDependencyReferences(claim, allClaims),
    () => checkPreamble(claim),
    () => checkLimitation(claim),
    () => checkPlaceholder(claim),
    () => checkSpecifyTokens(claim),
    () => checkClaimNumber(claim),
    () => checkIdfGrounding(claim, options),
    () => checkAntecedentBasis(claim, allClaims),
  ];
  const issues: HealthIssue[] = [];
  for (const runner of runners) {
    const r = runner();
    if (r) issues.push(r);
  }
  return {
    claim_id: claim.id,
    status: issues.length === 0 ? 'ok' : 'warn',
    issues,
    failed_checks: issues.map((i) => i.check_id),
  };
}

export function idfTextsFromState(matterState?: MatterFullState | null): string[] {
  const docs = (matterState as any)?.idf_documents as Array<{ extracted_text?: string }> | undefined;
  if (!docs) return [];
  return docs
    .map((d) => (d?.extracted_text || '').trim())
    .filter((t) => t.length > 0);
}

// ─── Text-highlight utility ────────────────────────────────────────────

export interface HighlightRange {
  start: number;
  end: number;
  message: string;
}

/**
 * Translate a set of ``{ highlight_text, message }`` pairs into
 * non-overlapping character ranges in ``text``. Only the first
 * occurrence of each ``highlight_text`` is matched — highlighting every
 * occurrence would be noisy and redundant given the hover tooltip
 * already lists the full issue.
 *
 * Overlap policy: earlier-found ranges win; later ranges that would
 * overlap are skipped.
 */
export function computeHighlightRanges(
  text: string,
  issues: HealthIssue[],
): HighlightRange[] {
  const ranges: HighlightRange[] = [];
  for (const issue of issues) {
    const needle = issue.highlight_text;
    if (!needle) continue;
    const idx = text.indexOf(needle);
    if (idx < 0) continue;
    const start = idx;
    const end = idx + needle.length;
    const overlaps = ranges.some((r) => !(end <= r.start || start >= r.end));
    if (overlaps) continue;
    ranges.push({ start, end, message: issue.message });
  }
  ranges.sort((a, b) => a.start - b.start);
  return ranges;
}
